import { type FormEvent, useState } from "react";
import {
	Badge,
	Button,
	Callout,
	Card,
	Field,
	PageHeader,
	TextArea,
	TextInput,
} from "../../../ui/primitives";
import {
	type LandingPageGenerator,
	type LandingPageInput,
	type LandingPageOutput,
	mockLandingPageGenerator,
} from "./generator";

const initialInput: LandingPageInput = {
	industry: "地域密着のベーカリー",
	product: "国産小麦を使った季節の焼き菓子ギフト",
	objective: "予約注文を増やす",
	tone: "親しみ",
};

export function UcsWeb001Page({
	generator = mockLandingPageGenerator,
}: {
	generator?: LandingPageGenerator;
}) {
	const [input, setInput] = useState(initialInput);
	const [output, setOutput] = useState<LandingPageOutput | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setBusy(true);
		setError(null);
		try {
			setOutput(await generator.generate(input));
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : "LPの生成に失敗しました");
		} finally {
			setBusy(false);
		}
	}

	function update(key: keyof LandingPageInput, value: string) {
		setInput((current) => ({ ...current, [key]: value }));
	}

	return (
		<>
			<PageHeader
				eyebrow="UCS-WEB-001"
				title="LP構成とコピーを作る"
				description="4つの項目から、セクション構成・コピー・CTAをまとめた下書きを作成します。"
				actions={<Badge tone="brand">Mock</Badge>}
			/>

			<div className="grid gap-5 xl:grid-cols-[minmax(300px,420px)_1fr]">
				<Card title="作成条件" description="未確定の情報は、分かる範囲で入力してください。">
					<form className="space-y-4" onSubmit={submit}>
						<Field label="業種">
							<TextInput
								required
								value={input.industry}
								onChange={(event) => update("industry", event.target.value)}
							/>
						</Field>
						<Field label="商材">
							<TextArea
								required
								rows={3}
								value={input.product}
								onChange={(event) => update("product", event.target.value)}
							/>
						</Field>
						<Field label="目的" hint="例: 無料相談、予約、応募を増やす">
							<TextInput
								required
								value={input.objective}
								onChange={(event) => update("objective", event.target.value)}
							/>
						</Field>
						<Field label="トーン" hint="例: 信頼感、親しみ、上質">
							<TextInput
								required
								value={input.tone}
								onChange={(event) => update("tone", event.target.value)}
							/>
						</Field>
						<Button type="submit" disabled={busy} className="w-full">
							{busy ? "生成しています…" : "LPの下書きを生成"}
						</Button>
					</form>
					{error && (
						<div className="mt-4">
							<Callout tone="danger" title="生成できませんでした">
								{error}
							</Callout>
						</div>
					)}
				</Card>

				<div className="space-y-5">
					{output ? (
						<LandingPagePreview output={output} />
					) : (
						<Card title="プレビュー">
							<div className="py-14 text-center text-[14px] text-ink-500">
								条件を確認して「LPの下書きを生成」を押してください。
							</div>
						</Card>
					)}
				</div>
			</div>
		</>
	);
}

function LandingPagePreview({ output }: { output: LandingPageOutput }) {
	return (
		<>
			<Card
				title="LPプレビュー"
				description={output.strategy.targetAudience}
				action={<Badge tone="success">生成済み</Badge>}
			>
				<div data-testid="lp-preview" className="space-y-6">
					{output.sections.map((section, index) => (
						<section
							key={section.id}
							className={index === 0 ? "rounded-xl bg-brand-50 px-5 py-6" : "px-1"}
						>
							<p className="text-[11px] font-semibold uppercase tracking-wider text-brand-600">
								{section.type}
							</p>
							<h2 className="mt-1 text-[18px] font-bold text-ink-900">{section.headline}</h2>
							<p className="mt-2 text-[14px] leading-7 text-ink-600">{section.body}</p>
							{section.cta && (
								<span className="mt-3 inline-flex rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white">
									{section.cta}
								</span>
							)}
						</section>
					))}
				</div>
			</Card>

			<Card title="構造化JSON" description="後続のデザイン・公開工程へ渡せる形式です。">
				<pre
					data-testid="lp-structured-output"
					className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-ink-900 p-4 text-[12px] leading-5 text-white"
				>
					{JSON.stringify(output, null, 2)}
				</pre>
			</Card>
		</>
	);
}
